import React, { useEffect, useRef, useState } from "react"

import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { useRecoilValue } from "recoil"

import { activePluginSelector } from "../../../recoil/pluginStudio/selectors"
import {
  isCustomView,
  PluginFile,
  UserRole,
} from "../../../recoil/pluginStudio/types"

interface CustomViewsDropdownProps {
  activeView: UserRole | string
  onViewChange: (view: UserRole | string, file?: PluginFile) => void
  className?: string
  size?: "sm" | "md" | "lg"
}

/**
 * Component for displaying custom views in a dropdown
 */
export const CustomViewsDropdown: React.FC<CustomViewsDropdownProps> = ({
  activeView,
  onViewChange,
  className = "",
  size = "md",
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const plugin = useRecoilValue(activePluginSelector)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Get custom views from plugin
  const getCustomViews = () => {
    if (!plugin?.files || !Array.isArray(plugin.files)) {
      return []
    }

    return plugin.files
      .filter((file) => isCustomView(file))
      .map((file) => ({
        ...file,
        id: file.id,
        name: file.name.replace("View.tsx", ""), // Remove the View.tsx suffix for display
        fileName: file.name,
        role: file.type,
      }))
  }

  const customViews = getCustomViews()

  // If no custom views, don't render anything
  if (customViews.length === 0) {
    return null
  }

  // Size classes
  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1 text-sm",
    lg: "px-4 py-2 text-base",
  }

  const buttonSizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1 text-sm",
    lg: "px-4 py-2 text-base",
  }

  const isActiveCustomView = customViews.some(
    (view) => view.fileName === activeView,
  )

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        className={`
          ${buttonSizeClasses[size]}
          flex items-center space-x-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded
          ${isActiveCustomView ? "bg-purple-600 text-white" : ""}
        `}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>Custom Views</span>
        <ChevronDownIcon
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
        {customViews.length > 0 && (
          <span className="ml-1 bg-gray-600 text-white text-xs rounded-full px-1.5 py-0.5">
            {customViews.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 border border-gray-700 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
          {customViews.map((view) => (
            <button
              key={view.id}
              className={`
                ${sizeClasses[size]}
                w-full text-left hover:bg-gray-700 flex items-center justify-between
                ${activeView === view.fileName ? "bg-blue-600 text-white" : "text-gray-300"}
              `}
              onClick={() => {
                onViewChange(UserRole.CUSTOM, view)
                setIsOpen(false)
              }}
            >
              <span className="truncate">{view.name}</span>
              <span className="text-xs text-gray-500 ml-2">
                {view.role === UserRole.GUEST
                  ? "Guest"
                  : view.role === UserRole.HOST_EVENT_VIEW
                    ? "Host"
                    : view.role === UserRole.HOST_CONFIG_VIEW
                      ? "Config"
                      : "Custom"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
